import { useState } from "react";
import { Link } from "react-router";
import type { CalendarItem, Project, Task } from "../../api/hub/types";
import type { Approval, OkrObjective, Post } from "../../api/hub/collaboration";
import { readPost } from "../../api/hub/collaboration";
import type { AiAction } from "../../api/hub/ai";
import type { HomeWidget } from "../../api/hub/home";
import { MutationState, SourceLink, useHubMutation } from "../focus/shared";
import { SandboxApp } from "../apps";
import { DataSource, DataState, MoreButton } from "./Frame";
import { PriorityIcon } from "../../ui/Priority";
import {
  calendarRange,
  dateLabel,
  dayStart,
  listItems,
  useDataList,
  useWorkspaceMe,
} from "./data";
import { FocusToday } from "./FocusToday";
import { readableOkrPeriod } from "./okrLabels";
type Config = Record<string, unknown>;
function pageSize(config: Config) {
  return typeof config.max === "number" ? config.max : 5;
}
const taskStates: Record<string, string> = {
  triage: "待分类",
  todo: "待办",
  in_progress: "进行中",
  in_review: "待验收",
  done: "已完成",
  canceled: "已取消",
};
export function TasksContent({ config = {} }: { config?: Config }) {
  const status = typeof config.status === "string" ? config.status : "all";
  const query = useDataList<Task>("tasks_mine", {
    limit: pageSize(config),
    ...(status === "overdue"
      ? { overdue: true }
      : status !== "all"
      ? { status }
      : {}),
  });
  const items = listItems(query.data?.pages);
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty={
        <>
          当前范围没有任务。<Link to="/tasks">查看全部任务 ↗</Link>
        </>
      }
    >
      {items.map((item) => (
        <article className="home-data-row" key={item.id}>
          <div className="home-row-meta">
            <span className="home-pri" data-urgent={item.priority === 1}>
              <PriorityIcon priority={item.priority} label={false} />
              {["未定", "紧急", "高", "中", "低"][item.priority]}
            </span>
            <span>{taskStates[item.status] ?? item.status}</span>
          </div>
          <h3>
            <Link to={"/tasks?task=" + encodeURIComponent(item.id)}>
              {item.title}
            </Link>
          </h3>
          <p className="home-muted">
            截止 {item.dueDate || "未设置"}
            {item.identifier ? " · " + item.identifier : ""}
          </p>
        </article>
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/tasks" />
    </DataState>
  );
}
export function RisksContent({ config = {} }: { config?: Config }) {
  const me = useWorkspaceMe(),
    query = useDataList<Project>("projects_list", { limit: pageSize(config) });
  const all = listItems(query.data?.pages),
    specified = Array.isArray(config.projectIds)
      ? config.projectIds
      : undefined;
  const items = all.filter((project) =>
    project.health !== "on_track" &&
    (specified?.length
      ? specified.includes(project.id)
      : !me.data || project.ownerId === me.data.data.id ||
        project.memberIds?.includes(me.data.data.id))
  );
  return (
    <DataState query={query}>
      <>
        {!items.length && (
          <p className="home-empty">
            {query.hasNextPage
              ? "当前已读取的项目没有已报告风险，继续查看更多。"
              : "关注项目没有已报告的风险。"}
          </p>
        )}
        {items.map((item) => (
          <article className="home-risk-row" key={item.id}>
            <div>
              <span
                className={"home-tag " + (item.health === "off_track"
                  ? "home-danger-tag"
                  : "home-warn-tag")}
              >
                {item.health === "off_track" ? "偏离计划" : "有风险"}
              </span>
              <h3>
                <Link to={"/projects?project=" + encodeURIComponent(item.id)}>
                  {item.title}
                </Link>
              </h3>
            </div>
            <div>
              <p className="home-muted">
                {item.targetDate
                  ? "目标交付 " + item.targetDate
                  : "目标交付日期未设置"}
              </p>
              <p className="home-muted">
                {Number.isFinite(item.progress)
                  ? "已完成 " + item.progress + "%"
                  : "进度待核对"}
              </p>
            </div>
            <Link to={"/project-agents?project=" + encodeURIComponent(item.id)}>
              看管家与依赖 ↗
            </Link>
          </article>
        ))}
        <MoreButton query={query} />
        <DataSource result={query.data?.pages.at(-1)} href="/projects" />
      </>
    </DataState>
  );
}
export function ApprovalsContent({ config = {} }: { config?: Config }) {
  const query = useDataList<Approval>("approvals_pending", {
      limit: pageSize(config),
    }),
    items = listItems(query.data?.pages);
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty="没有待你审批的事项。新的申请会显示在这里。"
    >
      {items.map((item) => (
        <article className="home-approval-row" key={item.id}>
          <div>
            <h3>{item.title}</h3>
            <p className="home-muted">
              {item.dueDate
                ? "截止 " + item.dueDate
                : "核对依据与授权范围后决定"}
            </p>
            <span className="home-tag home-warn-tag">待你决定</span>
          </div>
          <Link to={"/approvals?approval=" + encodeURIComponent(item.id)}>
            核对 ↗
          </Link>
        </article>
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/approvals" />
    </DataState>
  );
}
function Progress({ value, label }: { value: number; label: string }) {
  const known = Number.isFinite(value);
  return known
    ? (
      <>
        <div
          className="home-progress"
          role="progressbar"
          aria-label={label}
          aria-valuenow={Math.round(value)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <span style={{ width: Math.max(0, Math.min(100, value)) + "%" }} />
        </div>
        <p className="home-progress-meta">
          {Math.round(value)}% · {value >= 100 ? "已达成" : "持续推进"}
        </p>
      </>
    )
    : <p className="home-muted">进度尚未记录</p>;
}
const confidenceLabel = (value: string) => ({
  "-1": "未评估",
  "0": "正常",
  "1": "有风险",
  "2": "已延期",
  unassessed: "未评估",
  normal: "正常",
  on_track: "正常",
  at_risk: "有风险",
  off_track: "已延期",
}[value] ?? (value || "未评估"));
export function OkrContent({ config = {} }: { config?: Config }) {
  // Period IDs are opaque. The canonical source already returns active objectives; never construct a quarter ID in the browser.
  const query = useDataList<OkrObjective>("okr_mine", {
      limit: 5,
      ...(typeof config.period === "string" && config.period
        ? { period: config.period }
        : {}),
    }),
    items = listItems(query.data?.pages);
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty={
        <>
          当前周期还没有你负责的目标。<Link to="/okr">查看与设置 OKR ↗</Link>
        </>
      }
    >
      {items.map((objective) => (
        <article key={objective.id} className="home-okr-objective">
          {readableOkrPeriod(objective.period) && (
            <span className="home-muted">
              {readableOkrPeriod(objective.period)}
            </span>
          )}
          <h3>
            <Link to={"/okr?objective=" + encodeURIComponent(objective.id)}>
              {objective.title}
            </Link>
          </h3>
          <Progress value={objective.progress} label={objective.title} />
          {!!objective.keyResults?.length && (
            <details className="home-okr-results">
              <summary>{objective.keyResults.length} 个关键结果</summary>
              {objective.keyResults.map((kr) => (
                <div className="home-kr" key={kr.id}>
                  <p>{kr.title}</p>
                  <Progress value={kr.progress} label={kr.title} />
                  {kr.confidence && (
                    <p className="home-muted">
                      达成信心：{confidenceLabel(kr.confidence)}
                    </p>
                  )}
                </div>
              ))}
            </details>
          )}
        </article>
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/okr" />
    </DataState>
  );
}
export function CalendarContent({ config = {} }: { config?: Config }) {
  const me = useWorkspaceMe(),
    mode = config.mode === "month" ? "month" : "week",
    range = calendarRange(mode, me.data?.data.timezone ?? "Asia/Shanghai");
  const query = useDataList<CalendarItem>("calendar_range", {
      ...range,
      limit: 8,
    }, me.isSuccess),
    items = listItems(query.data?.pages);
  if (me.isError) return <DataState query={me}>{null}</DataState>;
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty={
        <>
          这段时间没有记录在中台的安排。<Link to="/calendar">打开月历 ↗</Link>
        </>
      }
    >
      <p className="home-muted">
        {range.from} 至 {range.to} · {me.data?.data.timezone}
      </p>
      {items.map((item) => (
        <article className="home-calendar-row" key={item.id}>
          <time>{item.date}</time>
          <div>
            <h3>
              <Link
                to={item.kind === "task"
                  ? "/tasks?task=" + encodeURIComponent(item.sourceId)
                  : "/calendar?date=" + encodeURIComponent(item.date)}
              >
                {item.title}
              </Link>
            </h3>
            <p className="home-muted">
              {({
                task: "任务截止",
                project: "项目目标",
                milestone: "里程碑",
                delivery: "交付",
                approval: "审批",
                okr: "目标提醒",
              })[item.kind] ?? item.kind}
            </p>
          </div>
        </article>
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/calendar" />
    </DataState>
  );
}
function InspirationRow({ item, required }: { item: Post; required: boolean }) {
  const me = useWorkspaceMe(),
    read = useHubMutation(async () => readPost(item.id));
  return (
    <article className={required ? "home-reading" : "home-data-row"}>
      <p className="home-row-meta">
        {item.authorName || "同事分享"} ·{" "}
        {dateLabel(item.createdAt, me.data?.data.timezone)}
        {required && " · 指定必读"}
      </p>
      <h3>
        <Link to={"/inspiration?post=" + encodeURIComponent(item.id)}>
          {item.title || item.opinion?.slice(0, 80) || "灵感分享"}
        </Link>
      </h3>
      {item.quote && <blockquote>{item.quote}</blockquote>}
      {item.opinion && <p>{item.opinion}</p>}
      <div className="home-actions">
        <SourceLink href={item.url}>核对原文 ↗</SourceLink>
        {required && (
          <button
            disabled={read.isPending}
            onClick={() => read.mutate()}
          >
            我已读
          </button>
        )}
      </div>
      <MutationState mutation={read} />
    </article>
  );
}
export function InspirationContent(
  { config = {}, required = true }: { config?: Config; required?: boolean },
) {
  const query = useDataList<Post>(
      required ? "inspiration_must_read" : "inspiration_feed",
      { limit: pageSize(config) },
    ),
    items = listItems(query.data?.pages);
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty={required ? "没有尚未阅读的必读材料。" : "当前没有同事分享。"}
    >
      {items.map((item) => (
        <InspirationRow key={item.id} item={item} required={required} />
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/inspiration" />
    </DataState>
  );
}
export function AiContent({ config = {} }: { config?: Config }) {
  const [expanded, setExpanded] = useState(false);
  const me = useWorkspaceMe(),
    since = dayStart(me.data?.data.timezone ?? "Asia/Shanghai");
  const query = useDataList<AiAction>("ai_actions_recent", {
      limit: pageSize(config),
      since,
    }, me.isSuccess),
    items = listItems(query.data?.pages);
  if (me.isError) return <DataState query={me}>{null}</DataState>;
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty="当前没有可见的 AI 执行记录。"
    >
      {config.compact === true &&
        items.filter((item) => item.error).map((item) => (
          <p key={item.id} className="home-error" role="alert">
            AI 执行失败：{item.error}
          </p>
        ))}
      {config.compact === true && (
        <p className="home-ai-summary">
          今天已记录 {items.length}
          {query.hasNextPage ? "+" : ""} 次动作 · {items.filter((item) =>
            ["needs_review", "suggested", "pending", "undoing"].includes(
              item.status ?? "",
            )
          ).length} 次待核对{" "}
          <button
            className="home-quiet"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "收起记录" : "查看记录"}
          </button>
        </p>
      )}
      {(!config.compact || expanded) &&
        items.map((item) => (
          <article className="home-ai-row" key={item.id}>
            <time>{dateLabel(item.createdAt, me.data?.data.timezone)}</time>
            <div>
              <h3>{item.reason || item.type}</h3>
              <p className="home-muted">
                {item.type} · {item.status === "needs_review"
                  ? "需要人核对"
                  : item.status === "failed"
                  ? "执行失败"
                  : item.status === "suggested"
                  ? "建议，尚未生效"
                  : item.undoneAt
                  ? "已撤销"
                  : item.status === "pending"
                  ? "正在准备"
                  : "已记录"}
              </p>
              {item.error && <p className="home-error">{item.error}</p>}
              <SourceLink
                href={item.sourceUrl ?? (item.entityType === "task"
                  ? "/tasks?task=" + encodeURIComponent(item.entityId)
                  : "/focus")}
              >
                查看执行记录 ↗
              </SourceLink>
            </div>
          </article>
        ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/focus" />
    </DataState>
  );
}
export function CrmContent({ config = {} }: { config?: Config }) {
  const query = useDataList<
      { id: string; name: string; status?: string; stage?: string }
    >("crm_overview", { limit: pageSize(config) }),
    items = listItems(query.data?.pages);
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty="当前授权范围内没有客户记录。"
    >
      {items.map((item) => (
        <article className="home-data-row" key={item.id}>
          <h3>
            <Link to={"/customers?customer=" + encodeURIComponent(item.id)}>
              {item.name}
            </Link>
          </h3>
          {(item.status || item.stage) && (
            <p className="home-muted">{item.stage || item.status}</p>
          )}
        </article>
      ))}
      <MoreButton query={query} />
      <DataSource result={query.data?.pages.at(-1)} href="/customers" />
    </DataState>
  );
}
export function QuickLinks({ config }: { config: Config }) {
  const links = Array.isArray(config.links)
    ? config.links as { label: string; href: string }[]
    : [];
  return links.length
    ? (
      <div className="home-quick-links">
        {links.map((link, i) => (
          <SourceLink href={link.href} key={i}>{link.label}</SourceLink>
        ))}
      </div>
    )
    : (
      <p className="home-empty">
        编辑这个部件，为常用的中台或允许的网站添加入口。
      </p>
    );
}
export function ActivityContent() {
  const [filter, setFilter] = useState("all");
  return (
    <>
      <div className="home-feed-filters" aria-label="动态筛选">
        {[["all", "全部"], ["team", "同事"], ["ai", "AI"]].map((
          [key, label],
        ) => (
          <button
            key={key}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="home-activity-columns">
        {filter !== "team" && (
          <section aria-label="AI 执行记录">
            <h3>AI 的执行记录</h3>
            <AiContent />
          </section>
        )}
        {filter !== "ai" && (
          <section aria-label="同事分享">
            <h3>同事的分享</h3>
            <InspirationContent required={false} config={{ max: 3 }} />
          </section>
        )}
      </div>
    </>
  );
}
export function WidgetContent(
  { widget, sharedOnly = false }: { widget: HomeWidget; sharedOnly?: boolean },
) {
  const config = widget.config;
  if (widget.type.startsWith("app:")) {
    return (
      <SandboxApp
        appId={widget.type.slice(4)}
        config={config}
        compact
        sharedOnly={sharedOnly}
      />
    );
  }
  switch (widget.type) {
    case "native:focus-today":
      return <FocusToday max={pageSize(config)} compact />;
    case "native:tasks-mine":
      return <TasksContent config={config} />;
    case "native:project-risks":
      return <RisksContent config={config} />;
    case "native:approvals-pending":
      return <ApprovalsContent config={config} />;
    case "native:okr-mine":
      return <OkrContent config={config} />;
    case "native:calendar":
      return <CalendarContent config={config} />;
    case "native:inspiration-must-read":
      return <InspirationContent config={config} />;
    case "native:ai-actions-recent":
      return <AiContent config={config} />;
    case "native:crm-overview":
      return <CrmContent config={config} />;
    case "native:quick-links":
      return <QuickLinks config={config} />;
    case "native:text-note":
      return (
        <p className="home-note">
          {typeof config.text === "string" && config.text
            ? config.text
            : "留一句提醒，或把今天需要想清楚的事写下来。"}
        </p>
      );
    default:
      return null;
  }
}
